import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { Loader2 } from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';
import { ArrowLeftLine } from '@archipro-design/icons';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { getMatchedModalOpenAtom } from '~/state/ui-state-get-matched-modal';
import { useGetMatchedSubmission } from '~/modules/get-matched/hooks/use-get-matched-submission';
import { useBuildHubProjectsForMatch } from '~/modules/get-matched/hooks/use-buildhub-projects-for-match';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import {
    useAuthGateSegment,
    type AuthGateSegment,
} from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { trackAuthGateActionResumed } from '~/modules-rocco/user/auth-gate/auth-gate-events';
import { useSocialAuthReturn } from '~/modules-rocco/user/auth-gate/use-social-auth-return';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { getAuthBrandStatement } from '~/modules-rocco/user/component/auth-modal/auth-intent-copy';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { isReturningPasskeyDevice } from '~/modules/user/passkey/passkey-device-match';
import { fileStorage } from '~/utils/file-storage';
import {
    GET_MATCHED_GATE_FILE_SESSION,
    useGetMatchedGateBrief,
    type GetMatchedGateBriefAnswers,
} from '~/modules-rocco/get-matched/get-matched-gate-state';
import {
    findBestMatch,
    budgetBucketFromRange,
    normalizeScope,
    type MatchScore,
} from '~/modules/get-matched/utils/match-buildhub-project';
import { GetMatchedIntroModal } from './GetMatchedIntroModal';
import { Step0EnquiryType } from './Step0EnquiryType';
import { Step1UserType } from './Step1UserType';
import { Step2Location } from './Step2Location';
import { Step3ProjectType } from './Step3ProjectType';
import { Step4ProjectScope } from './Step4ProjectScope';
import {
    Step4ProductCategory,
    type ProductCategoryOption,
} from './Step4ProductCategory';
import { Step4Style } from './Step4Style';
import { Step5Budget } from './Step5Budget';
import { Step6Project } from './Step6Project';
import { StepAccountConfirm } from './StepAccountConfirm';
import { Step8Done } from './Step8Done';
import { StepMatchProject } from './StepMatchProject';
import {
    ENQUIRY_CONFIG,
    SELECTOR_PANE_STATEMENT,
    type EnquiryType,
    type StepKey,
} from './enquiryConfig';

/**
 * Build the step list for the active enquiry type. Prepends a type-selector
 * step when no `defaultEnquiryType` is set. The `matchProject` step rides on
 * `projectScope`; flows without it (product, project) skip the BuildHub match.
 */
const buildStepFlow = ({
    enquiryType,
    defaultEnquiryType,
    matched,
}: {
    enquiryType: EnquiryType | null;
    defaultEnquiryType: EnquiryType | undefined;
    matched: MatchScore | null;
}): StepKey[] => {
    const baseFlow = enquiryType
        ? ENQUIRY_CONFIG[enquiryType].flow
        : ENQUIRY_CONFIG.professional.flow;

    const withMatch =
        matched && baseFlow.includes('projectScope')
            ? insertAfter(baseFlow, 'projectScope', 'matchProject')
            : baseFlow;

    return defaultEnquiryType ? withMatch : ['enquiryType', ...withMatch];
};

const insertAfter = (
    flow: StepKey[],
    anchor: StepKey,
    insert: StepKey,
): StepKey[] => {
    const idx = flow.indexOf(anchor);
    if (idx < 0) return flow;
    return [...flow.slice(0, idx + 1), insert, ...flow.slice(idx + 1)];
};

// BuildHub serialises Datetime as `YYYY-MM-DD HH:MM:SS`; <input type="date">
// only renders a `YYYY-MM-DD` value.
const toDateInputValue = (raw: string | null): string | null => {
    if (!raw) return null;
    const date = raw.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

/** Names this gate on every funnel event as well as selecting its intent copy. */
const GET_MATCHED_GATE_AUTH_SOURCE = 'getMatched' as const;

/**
 * How long a dismissal stays blocked for an in-flight send before the user
 * gets their exits back. Long enough for a slow attachment upload, short
 * enough that a hung request is an annoyance rather than a trap.
 */
const SEND_BLOCK_TIMEOUT_MS = 15_000;

/**
 * The wizard's working answers: the persistable brief plus the in-memory
 * attachments. Extending the gate-state shape keeps the two from drifting —
 * a field added here without a storage story fails to compile.
 */
interface MatchAnswers extends GetMatchedGateBriefAnswers {
    attachments: File[];
}

const buildInitialAnswers = (
    defaultEnquiryType: EnquiryType | undefined,
): MatchAnswers => ({
    enquiryType: defaultEnquiryType ?? null,
    userType: null,
    region: null,
    projectType: null,
    projectScope: null,
    productCategory: null,
    style: null,
    budget: null,
    description: '',
    professionals: null,
    startDate: null,
    endDate: null,
    email: '',
    attachments: [],
    buildHubID: null,
});

interface StyleImage {
    style: string;
    imageUrl: string | null;
}

interface GetMatchedModalProps {
    /** Keyed by style title; rows without an uploaded image fall back to the grey placeholder tile. */
    styleImages?: StyleImage[];
    /**
     * Fixes the enquiry type at mount time. When omitted the modal opens with
     * a type-selector step (used on pages where the audience isn't known —
     * homepage, /app etc).
     */
    defaultEnquiryType?: EnquiryType;
    /**
     * Top-level product categories surfaced into the productCategory step of
     * the product flow. Pass from the consumer (e.g. /products route loader);
     * falls back to a small static list from `enquiryConfig.ts` when omitted.
     */
    productCategories?: ProductCategoryOption[];
}

export const GetMatchedModal = ({
    styleImages,
    defaultEnquiryType,
    productCategories,
}: GetMatchedModalProps) => {
    const [open, setOpen] = useAtom(getMatchedModalOpenAtom);
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [step, setStep] = useState(1);
    const [answers, setAnswers] = useState<MatchAnswers>(() =>
        buildInitialAnswers(defaultEnquiryType),
    );
    const [matchedProject, setMatchedProject] = useState<MatchScore | null>(
        null,
    );
    const [pendingMatchAnswers, setPendingMatchAnswers] =
        useState<MatchAnswers | null>(null);
    const submission = useGetMatchedSubmission();
    const userLogin = useUserLoggedIn();
    const accountEmail = userLogin.isLoggedIn
        ? (userLogin.user.Email ?? null)
        : null;

    // --- Auth gate (BC-4838): a guest reaching the account step gets the
    // shared auth segment. Wizard steps are the steps-before; the brief
    // submission is the steps-after, auto-run once a session exists.
    const tracker = useTracker();
    const {
        save: saveBrief,
        take: takeBrief,
        clear: clearBrief,
    } = useGetMatchedGateBrief();
    /**
     * The far side of a social round-trip this gate started. Single use — see
     * {@link useSocialAuthReturn}; the modal outlives the journey it describes.
     */
    const { take: takeSocialAuth, spend: spendSocialAuth } =
        useSocialAuthReturn(GET_MATCHED_GATE_AUTH_SOURCE);

    /** This journey authenticated through the gate (in-modal or social return). */
    const [gateAuthed, setGateAuthed] = useState(false);
    const [gateIsSignUp, setGateIsSignUp] = useState(false);
    const gateAuthedRef = useRef(false);
    /** The segment has been entered for this visit to the account step. */
    const gateEnteredRef = useRef(false);
    /**
     * An auto-submit is owed as soon as `isLoggedIn` hydrates. State, not a
     * ref: the social resume primes it from a promise callback after the
     * attachment restore, and only a render makes the release effect look
     * again.
     */
    const [gateAutoSubmitPrimed, setGateAutoSubmitPrimed] = useState(false);
    /** The auto-submit has fired; a re-render must not fire it again. */
    const gateSubmittedRef = useRef(false);
    /** The signed-in path's auto-send has fired for this visit to the step. */
    const loggedInSubmittedRef = useRef(false);
    /** The submit landed while the Secure Sign-In ask held the surface. */
    const pendingDoneRef = useRef(false);
    /** `AuthGateActionResumed(landed: true)` reported for this journey. */
    const resumedFiredRef = useRef(false);
    /** The modal has been open since the last close — see the close effect. */
    const hasGateOpenedRef = useRef(false);
    const totalStepsRef = useRef(0);
    const gateRef = useRef<AuthGateSegment | null>(null);

    const handleGateAuthenticated = useCallback(
        (result?: { isSignUp: boolean }) => {
            gateAuthedRef.current = true;
            setGateAuthed(true);
            setGateIsSignUp(!!result?.isSignUp);
            // Prime rather than submit: the submit reads the account email,
            // which has not revalidated yet at this moment. The release effect
            // below runs it behind the Secure Sign-In ask.
            setGateAutoSubmitPrimed(true);
        },
        [],
    );

    const handleGateResolved = useCallback(() => {
        if (pendingDoneRef.current) {
            pendingDoneRef.current = false;
            setStep(totalStepsRef.current);
        }
    }, []);

    /** Routed through a ref: the handler is defined below the hook call. */
    const handleBackFromGateRef = useRef<() => void>(() => {});
    const handleGateBack = useCallback(
        () => handleBackFromGateRef.current(),
        [],
    );

    const gate = useAuthGateSegment({
        authSource: GET_MATCHED_GATE_AUTH_SOURCE,
        onAuthenticated: handleGateAuthenticated,
        onResolved: handleGateResolved,
        onAuthBack: handleGateBack,
    });
    gateRef.current = gate;

    const flow = buildStepFlow({
        enquiryType: answers.enquiryType,
        defaultEnquiryType,
        matched: matchedProject,
    });
    const totalSteps = flow.length;
    totalStepsRef.current = totalSteps;
    const currentStepKey = flow[step - 1] ?? flow[0] ?? 'userType';
    // Fallback to the professional variant for child copy when the user
    // hasn't picked a type yet — keeps step components mountable without
    // null checks.
    const activeEnquiryType: EnquiryType =
        answers.enquiryType ?? defaultEnquiryType ?? 'professional';
    const variantConfig = ENQUIRY_CONFIG[activeEnquiryType];

    // Apollo isn't mounted on category pages, so the matcher's project list
    // comes through a remix-api loader via useFetcher.
    const projectsForMatch = useBuildHubProjectsForMatch();
    const { fetchProjects } = projectsForMatch;

    useEffect(() => {
        if (open && userLogin.isLoggedIn) {
            fetchProjects();
        }
    }, [open, userLogin.isLoggedIn, fetchProjects]);

    useEffect(() => {
        if (open) {
            hasGateOpenedRef.current = true;
            return;
        }
        setStep(1);
        setAnswers(buildInitialAnswers(defaultEnquiryType));
        setMatchedProject(null);
        setPendingMatchAnswers(null);
        submission.reset();
        setGateAuthed(false);
        setGateIsSignUp(false);
        setGateAutoSubmitPrimed(false);
        gateAuthedRef.current = false;
        gateEnteredRef.current = false;
        gateSubmittedRef.current = false;
        loggedInSubmittedRef.current = false;
        pendingDoneRef.current = false;
        resumedFiredRef.current = false;
        // Only on a close that follows an opening — "not open" is also the
        // state at first mount, and on a social return the stashed brief must
        // survive until the resume effect reads it.
        if (hasGateOpenedRef.current) {
            hasGateOpenedRef.current = false;
            // A dismissed brief must not ambush a later sign-in with a
            // submission the user walked away from — and the journey the
            // social signal described has ended with it.
            spendSocialAuth();
            clearBrief();
            void fileStorage.clearFiles(GET_MATCHED_GATE_FILE_SESSION);
        }
        gateRef.current?.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultEnquiryType, clearBrief, spendSocialAuth]);

    // Far side of the gate's social round-trip: restore the stashed brief,
    // re-open the wizard at the account step and hand the segment an
    // already-authenticated reader. `takeBrief` is atomic, so of the several
    // GetMatchedModal instances a page can mount, exactly one resumes — and
    // the brief submits exactly once.
    const gateResumeConsumedRef = useRef(false);
    useEffect(() => {
        if (gateResumeConsumedRef.current) return;
        if (!userLogin.isLoggedIn) return;
        gateResumeConsumedRef.current = true;

        // The social signal first, the brief second. `isLoggedIn` also flips
        // after IN-MODAL auth — with a brief freshly stashed by the gate
        // entry — and resuming there would consume the brief and re-enter a
        // segment that is already mid-journey. Only the redirect's return
        // params say this page load is the far side of a round-trip.
        const social = takeSocialAuth();
        if (!social.authReturn) return;

        const brief = takeBrief();
        if (!brief) return;

        const restored: MatchAnswers = {
            ...buildInitialAnswers(defaultEnquiryType),
            ...brief.answers,
            email: userLogin.user.Email ?? brief.answers.email ?? '',
            attachments: [],
        };
        setAnswers(restored);
        setOpen(true);

        // Compute the resume step against the restored enquiry type's flow —
        // a product-flow brief has a different account index than a
        // professional one.
        const resumeFlow = buildStepFlow({
            enquiryType: restored.enquiryType,
            defaultEnquiryType,
            matched: null,
        });
        const accountIndex = resumeFlow.indexOf('account');
        setStep(accountIndex >= 0 ? accountIndex + 1 : 1);

        gateAuthedRef.current = true;
        setGateAuthed(true);
        setGateIsSignUp(social.signUpReturn);
        gateEnteredRef.current = true;

        const primeAndEnter = () => {
            setGateAutoSubmitPrimed(true);
            // The segment fires AuthGateOpened(entry: 'authenticated') and
            // decides whether this device earns the Secure Sign-In ask; the
            // primed submit runs behind it either way.
            gateRef.current?.enterAuthenticated({
                isNewAccount: social.signUpReturn,
            });
        };

        if (brief.hasAttachments) {
            // Merge the files back before priming, so the submit can never
            // race the read and send the brief without them.
            void fileStorage
                .getFiles(GET_MATCHED_GATE_FILE_SESSION)
                .then((files) => {
                    setAnswers((prev) => ({ ...prev, attachments: files }));
                    void fileStorage.clearFiles(GET_MATCHED_GATE_FILE_SESSION);
                })
                .catch(() => undefined)
                .finally(primeAndEnter);
        } else {
            primeAndEnter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userLogin.isLoggedIn]);

    // A guest reaching the account step hands the surface to the auth
    // segment. The brief is stashed first — AuthPanel owns the social buttons,
    // so there is no later pre-redirect moment to persist in.
    useEffect(() => {
        if (!open) return;
        if (currentStepKey !== 'account') return;
        if (userLogin.isLoggedIn) return;
        if (gateEnteredRef.current) return;
        gateEnteredRef.current = true;
        const { attachments, ...briefAnswers } = answers;
        saveBrief(briefAnswers, attachments.length > 0);
        void (attachments.length > 0
            ? fileStorage.saveFiles(GET_MATCHED_GATE_FILE_SESSION, attachments)
            : fileStorage.clearFiles(GET_MATCHED_GATE_FILE_SESSION));
        gateRef.current?.open();
    }, [open, currentStepKey, userLogin.isLoggedIn, answers, saveBrief]);

    // A signed-in user has nothing to confirm: reaching the account step IS
    // the send, so the previous step's Continue submits the brief. Guarded to
    // the step transition — a page can mount several instances of this modal,
    // and only the one the user actually walked may fire a submission.
    const prevStepKeyRef = useRef<StepKey | null>(null);
    useEffect(() => {
        const cameFrom = prevStepKeyRef.current;
        prevStepKeyRef.current = currentStepKey;
        if (currentStepKey !== 'account') {
            loggedInSubmittedRef.current = false;
            return;
        }
        if (cameFrom === null || cameFrom === 'account') return;
        if (!open) return;
        if (!userLogin.isLoggedIn) return;
        if (gateAuthedRef.current) return;
        if (loggedInSubmittedRef.current) return;
        if (submission.status !== 'idle') return;
        loggedInSubmittedRef.current = true;
        handleSubmitInternal(answers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentStepKey, userLogin.isLoggedIn, submission.status]);

    // Releases the primed auto-submit once a session exists. Primed by the
    // in-modal auth step (and by the social-return resume), held until
    // `isLoggedIn` hydrates because the submit reads the account email; runs
    // behind the Secure Sign-In ask so the send stays instant.
    useEffect(() => {
        if (!gateAutoSubmitPrimed) return;
        if (!userLogin.isLoggedIn) return;
        if (gateSubmittedRef.current) return;
        if (submission.status !== 'idle') return;
        setGateAutoSubmitPrimed(false);
        gateSubmittedRef.current = true;
        handleSubmitInternal(answers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gateAutoSubmitPrimed, userLogin.isLoggedIn, submission.status]);

    useEffect(() => {
        if (submission.status !== 'success') return;
        if (gateAuthedRef.current && !resumedFiredRef.current) {
            // The submit that follows the gate's auth step is the resumed
            // action, reported before the Secure Sign-In branch below —
            // landing while the ask is on screen is this gate's normal path,
            // not an edge case.
            resumedFiredRef.current = true;
            trackAuthGateActionResumed({
                tracker,
                authSource: GET_MATCHED_GATE_AUTH_SOURCE,
                landed: true,
            });
            // A landed brief must not resubmit from a later resume.
            clearBrief();
            void fileStorage.clearFiles(GET_MATCHED_GATE_FILE_SESSION);
        }
        if (gateRef.current?.isActive) {
            // The ask still holds the surface — the done screen waits for the
            // segment to resolve.
            pendingDoneRef.current = true;
            return;
        }
        setStep(totalSteps);
    }, [submission.status, totalSteps, tracker, clearBrief]);

    // The auto-submit came back an error. The inline retry on the confirm
    // view takes it from here; the funnel just must not count an
    // authentication whose action never landed.
    const prevSubmissionStatusRef = useRef(submission.status);
    useEffect(() => {
        const prev = prevSubmissionStatusRef.current;
        prevSubmissionStatusRef.current = submission.status;
        if (submission.status !== 'error' || prev === 'error') return;
        if (!gateAuthedRef.current) return;
        trackAuthGateActionResumed({
            tracker,
            authSource: GET_MATCHED_GATE_AUTH_SOURCE,
            landed: false,
        });
    }, [submission.status, tracker]);

    // Warm the browser cache for the style-step photography so it's decoded
    // before the user reaches that step (otherwise images flash on first paint).
    useEffect(() => {
        if (!open || !styleImages?.length || typeof window === 'undefined') {
            return;
        }
        for (const row of styleImages) {
            if (row.imageUrl) {
                const img = new window.Image();
                img.src = row.imageUrl;
            }
        }
    }, [open, styleImages]);

    const styleImageMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        for (const row of styleImages ?? []) {
            if (row.imageUrl) {
                map[row.style] = row.imageUrl;
            }
        }
        return map;
    }, [styleImages]);

    const isSubmitting = submission.status === 'submitting';

    // A send in flight must not be dismissed by a stray tap, but a fetcher has
    // no timeout of its own — on a stalled connection that lock would be
    // permanent. Hold it only as long as a send could plausibly still be
    // running.
    const [sendBlockExpired, setSendBlockExpired] = useState(false);
    useEffect(() => {
        if (!isSubmitting) {
            setSendBlockExpired(false);
            return;
        }
        const timer = setTimeout(
            () => setSendBlockExpired(true),
            SEND_BLOCK_TIMEOUT_MS,
        );
        return () => clearTimeout(timer);
    }, [isSubmitting]);
    const dismissBlocked = isSubmitting && !sendBlockExpired;

    const close = () => {
        if (dismissBlocked) return;
        // Records the abandonment (and the passkey-ask skip) when the reader
        // leaves from inside the segment; a no-op when it holds nothing.
        gateRef.current?.close();
        setOpen(false);
    };

    /**
     * What the account step shows: the auth segment for guests, the sending
     * hold while the brief goes out, or — only when a send failed — the
     * confirm view with its inline retry. A guest's frame before the entry
     * effect opens the segment shows nothing: painting the sending hold there
     * would claim a send that has not started.
     */
    const accountStepView: 'pending' | 'gate' | 'sending' | 'retry' =
        submission.status === 'error' && !gate.isActive
            ? 'retry'
            : gate.isActive
              ? 'gate'
              : !userLogin.isLoggedIn && !gateAuthed
                ? 'pending'
                : 'sending';

    const runMatch = (next: MatchAnswers): MatchScore | null =>
        findBestMatch(
            {
                region: next.region,
                projectScope: next.projectScope,
                budget: next.budget,
                startDate: next.startDate,
                endDate: next.endDate,
            },
            projectsForMatch.projects,
        );

    // Callers that have just-computed values for the flow inputs (e.g. fresh
    // enquiryType after a selector pick, or a fresh matchedProject) pass the
    // resolved flow in. Default branch falls back to render-time closure values
    // — fine for back-button presses where nothing in-flight has changed.
    const goNext = (overrideFlow?: StepKey[]) =>
        setStep((s) => {
            const nextFlow =
                overrideFlow ??
                buildStepFlow({
                    enquiryType: answers.enquiryType,
                    defaultEnquiryType,
                    matched: matchedProject,
                });
            return Math.min(s + 1, nextFlow.length);
        });
    const goBack = () => {
        if (isSubmitting) return;
        setStep((s) => Math.max(s - 1, 1));
    };

    const handleBackFromGate = () => {
        // Leaving the wall back into the brief is an abandonment the funnel
        // should see; returning to the account step re-opens the gate.
        gateRef.current?.close();
        gateEnteredRef.current = false;
        goBack();
    };
    handleBackFromGateRef.current = handleBackFromGate;

    // Steps 1-4 auto-advance on click, so a fast click-through reaches Step 4
    // before the BuildHub fetch returns. Defer the advance when projects
    // aren't ready yet so the user doesn't land on `style` and then jump back
    // to `matchProject` mid-render.
    const goNextFromCurrent = (latestAnswers: MatchAnswers) => {
        let nextMatched = matchedProject;
        if (currentStepKey === 'projectScope' && userLogin.isLoggedIn) {
            if (
                projectsForMatch.projects.length === 0 &&
                projectsForMatch.loading
            ) {
                setPendingMatchAnswers(latestAnswers);
                return;
            }
            nextMatched = runMatch(latestAnswers);
            setMatchedProject(nextMatched);
        }
        goNext(
            buildStepFlow({
                enquiryType: latestAnswers.enquiryType,
                defaultEnquiryType,
                matched: nextMatched,
            }),
        );
    };

    useEffect(() => {
        if (!pendingMatchAnswers) return;
        if (projectsForMatch.loading) return;
        const next = pendingMatchAnswers;
        setPendingMatchAnswers(null);
        const nextMatched = runMatch(next);
        setMatchedProject(nextMatched);
        goNext(
            buildStepFlow({
                enquiryType: next.enquiryType,
                defaultEnquiryType,
                matched: nextMatched,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMatchAnswers, projectsForMatch.loading]);

    // Cap the wait so a stalled fetch never hangs the modal — give up and
    // advance without a match. Drop any stale suggestion and rebuild the flow
    // with `matched: null` so the `matchProject` step isn't shown.
    useEffect(() => {
        if (!pendingMatchAnswers) return;
        const next = pendingMatchAnswers;
        const timeout = window.setTimeout(() => {
            setPendingMatchAnswers(null);
            setMatchedProject(null);
            goNext(
                buildStepFlow({
                    enquiryType: next.enquiryType,
                    defaultEnquiryType,
                    matched: null,
                }),
            );
        }, 1500);
        return () => window.clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingMatchAnswers]);

    const updateAnswers = (patch: Partial<MatchAnswers>) =>
        setAnswers((prev) => ({ ...prev, ...patch }));

    const handleAcceptMatch = () => {
        if (!matchedProject) return;
        const project = matchedProject.project;
        setAnswers((prev) => {
            const inferredBudget =
                prev.budget == null
                    ? budgetBucketFromRange(project.Budget)
                    : prev.budget;
            const inferredRegion =
                prev.region == null || prev.region === ''
                    ? (project.LocationData?.City ??
                      project.LocationData?.Suburb ??
                      project.Location ??
                      null)
                    : prev.region;
            return {
                ...prev,
                region: inferredRegion,
                projectScope:
                    normalizeScope(project.ProjectScope) ?? prev.projectScope,
                budget: inferredBudget,
                description: project.MainDescription ?? prev.description,
                startDate:
                    toDateInputValue(project.StartDate) ?? prev.startDate,
                endDate: toDateInputValue(project.EndDate) ?? prev.endDate,
                buildHubID: project.ID,
            };
        });
        goNext();
    };

    const handleSkipMatch = () => {
        // User declined the suggestion — clear the link, keep their answers, advance.
        setAnswers((prev) => ({ ...prev, buildHubID: null }));
        goNext();
    };

    const handleSubmitInternal = (source: MatchAnswers) => {
        if (isSubmitting) return;
        const effectiveEmail = accountEmail ?? source.email;
        const effectiveEnquiryType =
            source.enquiryType ?? defaultEnquiryType ?? null;
        submission.submit({
            enquiryType: effectiveEnquiryType,
            userType: source.userType,
            region: source.region,
            projectType: source.projectType,
            projectScope: source.projectScope,
            style: source.style,
            budget: source.budget,
            description: source.description,
            // Product flow stores the chosen category in the same DB column
            // (`ProfessionalType`) that the other variants use for their
            // "what are you looking for" answer.
            professionalType:
                effectiveEnquiryType === 'product'
                    ? source.productCategory
                    : source.professionals,
            startDate: source.startDate,
            endDate: source.endDate,
            email: effectiveEmail,
            attachments: source.attachments,
            buildHubID: source.buildHubID,
        });
    };

    const handleSubmit = () => handleSubmitInternal(answers);

    // The footer only exists where it has a Continue to hold; the hint lifts
    // above it there.
    const hasFooter =
        currentStepKey === 'details' ||
        (currentStepKey === 'region' && variantConfig.step2.multiSelect);

    return (
        <>
            {/* Triggers open the intro card, not the brief — mounted here so
                every consumer of this component gets the interim step without
                wiring it up itself. */}
            <GetMatchedIntroModal />
            {/* The shell every gate renders into: the 977×635 two-pane card on
                desktop, the enquiry sheet's bottom drawer on mobile — swipe
                zone, keyboard tracking and all. */}
            <TwoPaneModal
                corners="rounded"
                open={open}
                onOpenChange={(next) => {
                    if (!next && dismissBlocked) return;
                    if (!next) gateRef.current?.close();
                    setOpen(next);
                }}
                title="Get Matched"
                dismissible={!dismissBlocked}
                onPointerDownOutside={(e) => {
                    if (dismissBlocked) e.preventDefault();
                }}
                scrollHintClassName={hasFooter ? 'bottom-24' : undefined}
                scrollResetKey={currentStepKey}
                // Card only: the sheet is chosen by display mode, so a
                // landscape phone measures over `md` and would inherit the
                // card's inset — every other gate keeps the sheet on its own
                // padding.
                bodyClassName={isMobile ? undefined : 'md:pt-10'}
                pane={
                    // While the questions are on the right the pane pitches
                    // the matching, not the account — that ask hasn't
                    // happened yet.
                    <AuthModalBrandPane
                        statement={
                            currentStepKey === 'account' ||
                            currentStepKey === 'done'
                                ? getAuthBrandStatement(
                                      GET_MATCHED_GATE_AUTH_SOURCE,
                                      isReturningPasskeyDevice(),
                                  )
                                : currentStepKey === 'enquiryType'
                                  ? SELECTOR_PANE_STATEMENT
                                  : variantConfig.paneStatement
                        }
                        image={variantConfig.paneImage}
                    />
                }
            >
                <StepPane
                    step={step}
                    currentStepKey={currentStepKey}
                    answers={answers}
                    onUpdate={updateAnswers}
                    onAdvance={() => goNextFromCurrent(answers)}
                    onAdvanceWith={goNextFromCurrent}
                    onBack={goBack}
                    onClose={close}
                    onSubmit={handleSubmit}
                    submitting={isSubmitting}
                    styleImageMap={styleImageMap}
                    errorMessage={submission.errorMessage}
                    confirmationEmail={accountEmail ?? answers.email}
                    matchedProject={matchedProject}
                    onAcceptMatch={handleAcceptMatch}
                    onSkipMatch={handleSkipMatch}
                    matchPending={pendingMatchAnswers !== null}
                    variantConfig={variantConfig}
                    productCategories={productCategories}
                    accountStepView={accountStepView}
                    gateElement={gate.element}
                    // For a signed-in user the details step's Continue
                    // IS the send — the button restates the promise
                    // that opened the flow.
                    continueLabel={
                        currentStepKey === 'details' && userLogin.isLoggedIn
                            ? 'Get Matched'
                            : 'Continue'
                    }
                    accountCreated={gateAuthed && gateIsSignUp}
                />
            </TwoPaneModal>
        </>
    );
};

interface StepPaneProps {
    step: number;
    currentStepKey: StepKey;
    answers: MatchAnswers;
    onUpdate: (patch: Partial<MatchAnswers>) => void;
    onAdvance: () => void;
    onAdvanceWith: (latest: MatchAnswers) => void;
    onBack: () => void;
    onClose: () => void;
    onSubmit: () => void;
    submitting: boolean;
    errorMessage: string | null;
    styleImageMap: Record<string, string>;
    confirmationEmail: string;
    matchedProject: MatchScore | null;
    onAcceptMatch: () => void;
    onSkipMatch: () => void;
    matchPending: boolean;
    variantConfig: (typeof ENQUIRY_CONFIG)[EnquiryType];
    productCategories?: ProductCategoryOption[];
    accountStepView: 'pending' | 'gate' | 'sending' | 'retry';
    gateElement: React.ReactNode;
    continueLabel: string;
    accountCreated: boolean;
}

const StepPane = ({
    step,
    currentStepKey,
    answers,
    onUpdate,
    onAdvance,
    onAdvanceWith,
    onBack,
    onClose,
    onSubmit,
    submitting,
    errorMessage,
    styleImageMap,
    confirmationEmail,
    matchedProject,
    onAcceptMatch,
    onSkipMatch,
    matchPending,
    variantConfig,
    productCategories,
    accountStepView,
    gateElement,
    continueLabel,
    accountCreated,
}: StepPaneProps) => {
    const isFirstStep = step === 1;
    // Back rides as a leading arrow (the auth steps' idiom); the shell owns
    // the scroll container, the hint and the sheet behaviour.
    const showBackArrow =
        !isFirstStep &&
        currentStepKey !== 'account' &&
        currentStepKey !== 'done' &&
        currentStepKey !== 'matchProject';

    return (
        <>
            {matchPending && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/85 backdrop-blur-sm">
                    <p className="text-[0.875rem] font-medium tracking-[-0.01em] text-[#1c1d1f]">
                        Checking your projects…
                    </p>
                </div>
            )}
            {showBackArrow && (
                <button
                    type="button"
                    aria-label="Back"
                    onClick={onBack}
                    disabled={submitting}
                    className="mb-4 flex size-8 shrink-0 items-center justify-center self-start rounded-full text-[#181818] hover:bg-black/5 disabled:opacity-60"
                >
                    <ArrowLeftLine className="size-5" />
                </button>
            )}
            <StepBody
                currentStepKey={currentStepKey}
                answers={answers}
                onUpdate={onUpdate}
                onAdvance={onAdvance}
                onAdvanceWith={onAdvanceWith}
                onBack={onBack}
                onClose={onClose}
                onSubmit={onSubmit}
                submitting={submitting}
                errorMessage={errorMessage}
                styleImageMap={styleImageMap}
                confirmationEmail={confirmationEmail}
                matchedProject={matchedProject}
                onAcceptMatch={onAcceptMatch}
                onSkipMatch={onSkipMatch}
                variantConfig={variantConfig}
                productCategories={productCategories}
                accountStepView={accountStepView}
                gateElement={gateElement}
                accountCreated={accountCreated}
            />
            <FooterActions
                currentStepKey={currentStepKey}
                showRegionContinue={variantConfig.step2.multiSelect}
                regionContinueDisabled={
                    !answers.region || answers.region.trim() === ''
                }
                onContinue={onAdvance}
                continueLabel={continueLabel}
                submitting={submitting}
            />
        </>
    );
};

interface StepBodyProps {
    currentStepKey: StepKey;
    answers: MatchAnswers;
    onUpdate: (patch: Partial<MatchAnswers>) => void;
    onAdvance: () => void;
    onAdvanceWith: (latest: MatchAnswers) => void;
    onBack: () => void;
    onClose: () => void;
    onSubmit: () => void;
    submitting: boolean;
    errorMessage: string | null;
    styleImageMap: Record<string, string>;
    confirmationEmail: string;
    matchedProject: MatchScore | null;
    onAcceptMatch: () => void;
    onSkipMatch: () => void;
    variantConfig: (typeof ENQUIRY_CONFIG)[EnquiryType];
    productCategories?: ProductCategoryOption[];
    accountStepView: 'pending' | 'gate' | 'sending' | 'retry';
    gateElement: React.ReactNode;
    accountCreated: boolean;
}

const StepBody = ({
    currentStepKey,
    answers,
    onUpdate,
    onAdvance,
    onAdvanceWith,
    onBack,
    onClose,
    onSubmit,
    submitting,
    errorMessage,
    styleImageMap,
    confirmationEmail,
    matchedProject,
    onAcceptMatch,
    onSkipMatch,
    variantConfig,
    productCategories,
    accountStepView,
    gateElement,
    accountCreated,
}: StepBodyProps) => {
    const selectAndAdvance = (patch: Partial<MatchAnswers>) => {
        // Switching enquiry type must blank variant-conditional answers so a
        // re-pick from the selector step can't leak (e.g.) a stale
        // "Architects" ProfessionalType into a product brief.
        const fullPatch: Partial<MatchAnswers> =
            patch.enquiryType && patch.enquiryType !== answers.enquiryType
                ? {
                      ...patch,
                      projectScope: null,
                      productCategory: null,
                      style: null,
                      professionals: null,
                      buildHubID: null,
                  }
                : patch;
        onUpdate(fullPatch);
        onAdvanceWith({ ...answers, ...fullPatch });
    };

    switch (currentStepKey) {
        case 'enquiryType':
            return (
                <Step0EnquiryType
                    value={answers.enquiryType}
                    onSelect={(enquiryType) =>
                        selectAndAdvance({ enquiryType })
                    }
                />
            );
        case 'userType':
            return (
                <Step1UserType
                    title={variantConfig.step1.title}
                    options={variantConfig.step1.options}
                    value={answers.userType}
                    onSelect={(userType) => selectAndAdvance({ userType })}
                />
            );
        case 'region':
            return (
                <Step2Location
                    title={variantConfig.step2.title}
                    helperText={variantConfig.step2.helperText}
                    multiSelect={variantConfig.step2.multiSelect}
                    value={answers.region}
                    // Multi-select updates the region in place without
                    // auto-advancing — the Continue button in FooterActions
                    // moves the flow on once the user is done picking.
                    onSelect={(region) =>
                        variantConfig.step2.multiSelect
                            ? onUpdate({ region })
                            : selectAndAdvance({ region })
                    }
                />
            );
        case 'projectType':
            return (
                <Step3ProjectType
                    title={variantConfig.step3.title}
                    options={variantConfig.step3.options}
                    value={answers.projectType}
                    onSelect={(projectType) =>
                        selectAndAdvance({ projectType })
                    }
                />
            );
        case 'projectScope':
            return (
                <Step4ProjectScope
                    value={answers.projectScope}
                    onSelect={(projectScope) =>
                        selectAndAdvance({ projectScope })
                    }
                />
            );
        case 'matchProject':
            return matchedProject ? (
                <StepMatchProject
                    match={matchedProject}
                    onAccept={onAcceptMatch}
                    onSkip={onSkipMatch}
                />
            ) : null;
        case 'productCategory': {
            const cfg = variantConfig.stepProductCategory;
            if (!cfg) return null;
            // Live options from the consumer (e.g. /products loader) take
            // precedence; fallback list keeps the modal usable when the
            // consumer hasn't wired the loader.
            const options: ProductCategoryOption[] =
                productCategories && productCategories.length > 0
                    ? productCategories
                    : cfg.fallbackOptions.map((label) => ({
                          value: label,
                          label,
                      }));
            return (
                <Step4ProductCategory
                    title={cfg.title}
                    options={options}
                    value={answers.productCategory}
                    onSelect={(productCategory) =>
                        selectAndAdvance({ productCategory })
                    }
                />
            );
        }
        case 'style':
            return (
                <Step4Style
                    value={answers.style}
                    onSelect={(style) => selectAndAdvance({ style })}
                    images={styleImageMap}
                />
            );
        case 'budget':
            return (
                <Step5Budget
                    title={variantConfig.step5Budget.title}
                    helperText={variantConfig.step5Budget.helperText}
                    options={variantConfig.step5Budget.options}
                    value={answers.budget}
                    onSelect={(budget) => selectAndAdvance({ budget })}
                />
            );
        case 'details':
            return (
                <Step6Project
                    title={variantConfig.step6.title}
                    lookingForField={variantConfig.step6.lookingForField}
                    showTimeline={variantConfig.step6.showTimeline}
                    descriptionLabel={variantConfig.step6.descriptionLabel}
                    descriptionPlaceholder={
                        variantConfig.step6.descriptionPlaceholder
                    }
                    timelineLabel={variantConfig.step6.timelineLabel}
                    timelineStartLabel={variantConfig.step6.timelineStartLabel}
                    timelineEndLabel={variantConfig.step6.timelineEndLabel}
                    value={{
                        description: answers.description,
                        professionals: answers.professionals,
                        startDate: answers.startDate,
                        endDate: answers.endDate,
                        attachments: answers.attachments,
                    }}
                    onChange={(next) => onUpdate(next)}
                />
            );
        case 'account': {
            // Only a failed send earns a screen with questions on it — the
            // confirm view and its inline retry. Everything else on this step
            // is the segment's own UI or the sending hold: guests see the
            // gate, and a signed-in user's Continue from the previous step
            // already was the send.
            if (accountStepView === 'retry') {
                return (
                    <StepAccountConfirm
                        confirmationPrefix={
                            variantConfig.step7.loggedInConfirmationPrefix
                        }
                        confirmationSuffix={
                            variantConfig.step7.loggedInConfirmationSuffix
                        }
                        onContinue={onSubmit}
                        onBack={onBack}
                        submitting={submitting}
                        errorMessage={errorMessage}
                    />
                );
            }
            if (accountStepView === 'gate' && gateElement) {
                // The auth step carries its own back arrow into the brief,
                // wired through the segment's onAuthBack.
                return <>{gateElement}</>;
            }
            if (accountStepView === 'sending') {
                return <GateSendingView />;
            }
            // The segment is entered from an effect; nothing may flash in the
            // frame before it takes the surface.
            return null;
        }
        case 'done':
            return (
                <Step8Done
                    onClose={onClose}
                    email={confirmationEmail}
                    matchSubjectPlural={variantConfig.step8.matchSubjectPlural}
                    accountCreated={accountCreated}
                />
            );
        default:
            return null;
    }
};

/**
 * Holds the surface between the gate's auth step and the done screen — the
 * same sending treatment the enquiry gate uses, so the pause after signing in
 * reads as the flow working rather than the wizard resetting.
 */
const GateSendingView = () => (
    <div className="mx-auto my-auto flex w-full max-w-90.75 flex-col items-center justify-center gap-4 py-16">
        <Loader2 className="size-9 animate-spin text-[#828282]" />
        <p className="text-[1.0625rem] text-[#828282]">Sending your brief…</p>
    </div>
);

interface FooterActionsProps {
    currentStepKey: StepKey;
    /** True when the active step is region AND the variant has multiSelect mode on. */
    showRegionContinue: boolean;
    /** Disable Continue on region step until at least one region is picked. */
    regionContinueDisabled: boolean;
    onContinue: () => void;
    /** "Get Matched" when the press is the send (signed-in details step). */
    continueLabel: string;
    submitting: boolean;
}

/**
 * Only the steps that need an explicit Continue get a footer at all: Back
 * rides as the leading arrow, and dismissal belongs to the dialog's own X.
 */
const FooterActions = ({
    currentStepKey,
    showRegionContinue,
    regionContinueDisabled,
    onContinue,
    continueLabel,
    submitting,
}: FooterActionsProps) => {
    const showContinue =
        currentStepKey === 'details' ||
        (currentStepKey === 'region' && showRegionContinue);
    if (!showContinue) return null;

    const continueDisabled =
        submitting ||
        (currentStepKey === 'region' &&
            showRegionContinue &&
            regionContinueDisabled);

    return (
        <GateStickyFooter className="mt-8" pinOn="both">
            <button
                type="button"
                onClick={onContinue}
                disabled={continueDisabled}
                className="flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:opacity-60"
            >
                {continueLabel}
            </button>
        </GateStickyFooter>
    );
};
